import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, Mail, RefreshCw, CheckCircle, XCircle, AlertTriangle, FileText, Inbox as InboxIcon, Trash2, Zap } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const TYPE_LABELS = {
  purchase_bill: 'Purchase Bill',
  expense_receipt: 'Expense Receipt',
  bank_statement: 'Bank Statement',
  todo_reminder: 'To-do / Reminder',
  other: 'Other',
};

export default function Inbox() {
  const [drafts, setDrafts] = useState([]);
  const [status, setStatus] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [config, setConfig] = useState(null);
  const [polling, setPolling] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        axios.get(`${API}/inbox?status=${status}`, { withCredentials: true }),
        axios.get(`${API}/inbox/config-status`, { withCredentials: true }),
      ]);
      setDrafts(a.data || []);
      setConfig(b.data);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const upload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        await axios.post(`${API}/inbox/upload`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 });
      } catch (e) { toast.error(`${file.name}: ${e.response?.data?.detail || 'upload failed'}`); }
    }
    setUploading(false);
    toast.success(`Uploaded ${files.length} file${files.length > 1 ? 's' : ''} — AI classifying…`);
    load();
  };

  const pollNow = async () => {
    setPolling(true);
    try {
      const r = await axios.post(`${API}/inbox/fetch-emails`, {}, { withCredentials: true });
      if (r.data.success) toast.success(`Pulled ${r.data.processed} new email(s) · skipped ${r.data.skipped}`);
      else toast.error(r.data.reason || 'Gmail fetch failed');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setPolling(false); }
  };

  const approve = async (d) => {
    try { await axios.post(`${API}/inbox/${d.id}/approve`, {}, { withCredentials: true });
      toast.success(`Promoted to ${TYPE_LABELS[d.extracted?.doc_type] || 'draft'}`); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const reject = async (d) => {
    if (!window.confirm('Reject this draft?')) return;
    try { await axios.post(`${API}/inbox/${d.id}/reject`, {}, { withCredentials: true }); toast.success('Rejected'); load(); }
    catch { toast.error('Failed'); }
  };
  const del = async (d) => {
    if (!window.confirm('Permanently delete this draft + stored file?')) return;
    try { await axios.delete(`${API}/inbox/${d.id}`, { withCredentials: true }); toast.success('Deleted'); load(); }
    catch { toast.error('Failed'); }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragActive(false);
    upload(Array.from(e.dataTransfer.files || []));
  };

  return (
    <div className="space-y-5" data-testid="inbox-page">
      <div>
        <h1 className="font-heading text-2xl font-semibold text-zinc-50 flex items-center gap-2"><InboxIcon size={18} className="text-amber-400" /> Universal Inbox</h1>
        <p className="text-zinc-500 text-xs mt-0.5">Drop bills, receipts, statements, or to-do scans here. AI classifies → you review → tick ✅ to save into the right section.</p>
      </div>

      {/* Source status / setup banner */}
      <div className="bg-gradient-to-br from-zinc-900 to-zinc-950 border border-amber-500/30 rounded-xl p-4 space-y-3" data-testid="inbox-config">
        <div className="flex items-center gap-3">
          <Mail size={18} className="text-amber-400" />
          <div className="flex-1">
            <div className="text-sm text-zinc-100 font-semibold">Auto-scan mailbox setup</div>
            <div className="text-[11px] text-zinc-500 mt-0.5">
              Three safe options to feed bills/invoices into the inbox. Pick whichever fits you.
              You never have to share your real Google password with the ERP.
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {/* Option A — forward-only mailbox */}
          <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-3">
            <div className="text-[11px] text-amber-300 font-semibold uppercase tracking-wider mb-1">Option A · Dedicated Gmail</div>
            <p className="text-zinc-300 text-[12px] leading-relaxed">
              Use a brand-new Gmail like <code className="text-amber-300">{config?.gmail_user || 'tirthindustries.erp@gmail.com'}</code>
              used <em>only</em> for bills. Auto-forward vendor bills to it from your main mailbox. ERP reads only this one.
            </p>
            <div className="text-[10px] text-zinc-500 mt-2">Status: {config?.gmail_configured ? <span className="text-green-300">✓ active</span> : <span className="text-zinc-500">needs app password</span>}</div>
          </div>
          {/* Option B — webhook / inbound route */}
          <div className="bg-zinc-950 border border-emerald-700/40 rounded-lg p-3">
            <div className="text-[11px] text-emerald-300 font-semibold uppercase tracking-wider mb-1 flex items-center gap-1.5">
              <Zap size={10} /> Option B · Inbound Webhook (safest)
            </div>
            <p className="text-zinc-300 text-[12px] leading-relaxed">
              Use a dedicated inbox like <code className="text-emerald-300">{config?.inbound_address || 'bills@your-domain'}</code>.
              Emails hit a webhook → attachments parsed → draft bills created automatically. <strong>No password stored.</strong>
            </p>
            {config?.inbound_webhook_configured ? (
              <div className="mt-2 space-y-1">
                <div className="text-[9px] text-zinc-500 uppercase">Webhook URL</div>
                <div className="bg-zinc-900 border border-zinc-800 rounded px-2 py-1 text-[10px] font-mono text-emerald-200 break-all" data-testid="inbound-webhook-url">
                  {config.inbound_webhook_url}?token=<em className="text-zinc-500">&lt;secret&gt;</em>
                </div>
                <button onClick={() => { navigator.clipboard?.writeText(config.inbound_webhook_url); toast.success('URL copied'); }}
                  data-testid="copy-webhook-url"
                  className="text-[10px] px-2 py-0.5 rounded bg-emerald-600 hover:bg-emerald-500 text-white font-semibold">
                  Copy base URL
                </button>
                <div className="text-[9px] text-zinc-500 mt-1">Paste into Resend → Inbound Routes, or SendGrid → Inbound Parse.</div>
              </div>
            ) : (
              <div className="text-[10px] text-zinc-500 mt-2">Webhook secret not configured — ask admin to set <code>INBOUND_WEBHOOK_SECRET</code>.</div>
            )}
          </div>
          {/* Option C — manual upload */}
          <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-3">
            <div className="text-[11px] text-sky-300 font-semibold uppercase tracking-wider mb-1">Option C · Manual drop-zone</div>
            <p className="text-zinc-300 text-[12px] leading-relaxed">
              Drag-drop bill PDFs/images here. AI extracts vendor / invoice# / amount / line-items and creates a draft.
            </p>
            <div className="text-[10px] text-zinc-500 mt-2">No credentials. Highest manual control.</div>
          </div>
        </div>
        {config?.gmail_configured && (
          <button onClick={pollNow} disabled={polling} data-testid="inbox-poll-now"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-amber-500 hover:bg-amber-400 text-black rounded-lg font-semibold disabled:opacity-50 self-start">
            <RefreshCw size={12} className={polling ? 'animate-spin' : ''} /> {polling ? 'Fetching…' : 'Fetch now'}
          </button>
        )}
      </div>

      {/* Drop zone */}
      <div onDragEnter={e => { e.preventDefault(); setDragActive(true); }}
           onDragOver={e => e.preventDefault()}
           onDragLeave={() => setDragActive(false)}
           onDrop={onDrop}
           onClick={() => fileRef.current?.click()}
           data-testid="inbox-dropzone"
           className={`rounded-2xl border-2 border-dashed p-10 text-center cursor-pointer transition-all ${dragActive ? 'border-amber-500 bg-amber-500/10' : 'border-zinc-700 hover:border-zinc-600 bg-zinc-900/40'}`}>
        <Upload size={28} className={`mx-auto mb-2 ${dragActive ? 'text-amber-400' : 'text-zinc-500'}`} />
        <div className="text-zinc-300 text-sm font-semibold">{dragActive ? 'Drop files to upload' : 'Drag & drop files — or click to pick'}</div>
        <div className="text-zinc-500 text-[11px] mt-1">PDF, PNG, JPG, WebP, TXT supported · up to 25 MB · {uploading && <span className="text-amber-300">uploading…</span>}</div>
        <input ref={fileRef} type="file" multiple hidden
          accept=".pdf,.png,.jpg,.jpeg,.webp,.txt"
          onChange={e => { upload(Array.from(e.target.files || [])); e.target.value = ''; }}
          data-testid="inbox-file-input" />
      </div>

      {/* Status tabs */}
      <div className="flex gap-2">
        {['pending', 'approved', 'rejected', 'all'].map(s => (
          <button key={s} onClick={() => setStatus(s)} data-testid={`inbox-status-${s}`}
            className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition-all ${status === s ? 'bg-amber-500/15 border-amber-500/40 text-amber-300' : 'bg-zinc-900 border-zinc-800 text-zinc-400 hover:text-zinc-200'}`}>
            {s.toUpperCase()}
          </button>
        ))}
      </div>

      {loading && <div className="text-zinc-500 text-sm animate-pulse">Loading…</div>}
      {!loading && drafts.length === 0 && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-10 text-center text-zinc-500 text-sm">
          No {status === 'all' ? '' : status} drafts.
        </div>
      )}

      {/* Draft cards */}
      <div className="space-y-3">
        {drafts.map(d => (
          <DraftCard key={d.id} draft={d} onApprove={approve} onReject={reject} onDelete={del} />
        ))}
      </div>
    </div>
  );
}

function DraftCard({ draft, onApprove, onReject, onDelete }) {
  const ext = draft.extracted || {};
  const typeColor = {
    purchase_bill: 'text-amber-300 bg-amber-900/30 border-amber-700/50',
    expense_receipt: 'text-blue-300 bg-blue-900/30 border-blue-700/50',
    bank_statement: 'text-purple-300 bg-purple-900/30 border-purple-700/50',
    todo_reminder: 'text-pink-300 bg-pink-900/30 border-pink-700/50',
    other: 'text-zinc-300 bg-zinc-800 border-zinc-700',
  }[ext.doc_type] || 'text-zinc-300 bg-zinc-800 border-zinc-700';

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 flex flex-col md:flex-row gap-4" data-testid={`draft-${draft.id}`}>
      {/* Header + extracted */}
      <div className="flex-1 min-w-0 space-y-2">
        <div className="flex flex-wrap gap-2 items-center">
          <span className={`text-[10px] px-2 py-0.5 rounded-full border capitalize ${typeColor}`}>{TYPE_LABELS[ext.doc_type] || ext.doc_type}</span>
          {ext.confidence !== undefined && <span className="text-[10px] text-zinc-500">confidence {(ext.confidence * 100).toFixed(0)}%</span>}
          {draft.source === 'email' ? <span className="text-[10px] text-zinc-500"><Mail size={10} className="inline mr-1" />from {draft.email_from?.slice(0, 30)}</span>
                                   : <span className="text-[10px] text-zinc-500"><FileText size={10} className="inline mr-1" />{draft.original_filename?.slice(0, 40)}</span>}
          <span className="text-[10px] text-zinc-500">{new Date(draft.uploaded_at).toLocaleString()}</span>
        </div>
        <div className="text-zinc-100 text-sm">{ext.summary || draft.email_subject || '(no summary)'}</div>
        {/* Extracted fields grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5 text-[11px]">
          {ext.vendor_name && <Field label="Vendor" value={ext.vendor_name} />}
          {ext.invoice_number && <Field label="Invoice #" value={ext.invoice_number} />}
          {ext.invoice_date && <Field label="Date" value={ext.invoice_date} />}
          {ext.total_amount > 0 && <Field label="Total" value={fmt(ext.total_amount)} color="text-emerald-300" />}
          {ext.gst_amount > 0 && <Field label="GST" value={fmt(ext.gst_amount)} />}
          {ext.hsn_code && <Field label="HSN" value={ext.hsn_code} />}
          {ext.suggested_category && <Field label="Category" value={ext.suggested_category} />}
          {ext.todo_text && <Field label="To-do" value={ext.todo_text} />}
        </div>
        {ext.red_flags?.length > 0 && (
          <div className="bg-red-900/20 border border-red-700/50 rounded p-2 text-[11px] text-red-200">
            <AlertTriangle size={11} className="inline mr-1" /> {ext.red_flags.join(' · ')}
          </div>
        )}
      </div>

      {/* Actions */}
      {draft.status === 'pending' && (
        <div className="flex md:flex-col gap-2 shrink-0 md:w-36">
          <button onClick={() => onApprove(draft)} data-testid={`draft-approve-${draft.id}`}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs bg-green-500/20 hover:bg-green-500/30 text-green-200 border border-green-700/50 rounded-lg font-semibold">
            <CheckCircle size={12} /> Approve
          </button>
          <button onClick={() => onReject(draft)} data-testid={`draft-reject-${draft.id}`}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-lg font-semibold">
            <XCircle size={12} /> Reject
          </button>
          <button onClick={() => onDelete(draft)} data-testid={`draft-delete-${draft.id}`}
            className="flex items-center justify-center gap-1 px-3 py-1.5 text-[11px] bg-red-900/30 hover:bg-red-900/50 text-red-300 rounded-lg">
            <Trash2 size={11} /> Delete
          </button>
        </div>
      )}
      {draft.status !== 'pending' && (
        <div className="shrink-0 text-[11px] text-zinc-500 md:w-36">
          <div className={`px-2 py-1 rounded ${draft.status === 'approved' ? 'bg-green-900/30 text-green-300' : 'bg-zinc-800 text-zinc-400'}`}>
            {draft.status.toUpperCase()}
          </div>
          {draft.promoted_to?.collection && <div className="mt-1">→ {draft.promoted_to.collection}</div>}
        </div>
      )}
    </div>
  );
}

function Field({ label, value, color = 'text-zinc-200' }) {
  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1">
      <div className="text-[9px] text-zinc-500 uppercase tracking-wider">{label}</div>
      <div className={`text-[11px] font-semibold truncate ${color}`} title={value}>{value}</div>
    </div>
  );
}
